/**
 * UpgradeBanner — Premium-Hinweis mit CTA zum License-Shop.
 *
 * Visuell konsistent zum bestehenden `LicenseRequiredRoute` in MyPortalApp.tsx —
 * gleiches Lock/Shield-Icon-Pattern, primary-600-Button, dark-mode-aware.
 *
 * Wird angezeigt, wenn eine Seite ein Premium-Feature erfordert, das der
 * Tenant nicht freigeschaltet hat. Der CTA leitet immer in den License-Shop
 * (/my/shop) — dort liegen alle Produkte.
 */

import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface UpgradeBannerProps {
  feature: string;
  requiredAddon?: string | null;
  title?: string;
  description?: string;
  ctaLabel?: string;
}

const DEFAULT_COPY: Record<string, { title: string; description: string; cta: string }> = {
  bisremote: {
    title: 'BIS Remote freischalten',
    description:
      'Fernwartung der Kasse aus dem Kundencenter: Einstellungen, Bonvorlagen, Favoriten und Kunden zentral pflegen — auch für mehrere Kassen gleichzeitig. 10 €/Monat, 14 Tage gratis testen.',
    cta: 'Zum Shop',
  },
  'bis-insights': {
    title: 'BIS Insights freischalten',
    description:
      'Detaillierte Auswertungen & Statistiken: Verkaufs-/Kunden-/Artikel-/Lager-Reports, Marktplatz-Performance, Gebühren-Analyse — plus alle POS-Berichte (Kassenjournal, UStVA, EÜR, BWA, DSFinV-K), der DATEV-Kassenexport aller Kassen gesammelt und unbegrenzte Cloud-Historie. Dazu der Monatsversand an den Steuerberater. 15 €/Monat, 30 Tage gratis testen.',
    cta: 'Zum Shop',
  },
  // Legacy-Alias (alter Name): bestehende Tenants mit licenses(productCode='bisremote-accounting')
  'bisremote-accounting': {
    title: 'BIS Insights freischalten',
    description:
      'Detaillierte Auswertungen & Statistiken: Verkaufs-/Kunden-/Artikel-/Lager-Reports, Marktplatz-Performance, Gebühren-Analyse — plus alle POS-Berichte (Kassenjournal, UStVA, EÜR, BWA, DSFinV-K), der DATEV-Kassenexport aller Kassen gesammelt und unbegrenzte Cloud-Historie. Dazu der Monatsversand an den Steuerberater. 15 €/Monat, 30 Tage gratis testen.',
    cta: 'Zum Shop',
  },
};

export function UpgradeBanner({
  feature,
  requiredAddon,
  title,
  description,
  ctaLabel,
}: UpgradeBannerProps) {
  const { t } = useTranslation();
  const copy = (requiredAddon && DEFAULT_COPY[requiredAddon]) || DEFAULT_COPY.bisremote;

  // Alle Produkte (BIS Remote, BIS Insights, sonstige) laufen über den
  // License-Shop. Die alte AddOn-Seite (/my/billing/addons) wurde entfernt.
  const shopHref = '/my/shop';

  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <Lock size={48} className="text-gray-400 mb-4" />
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        {title ?? copy.title}
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-2xl">
        {description ?? copy.description}
      </p>
      <Link
        to={shopHref}
        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        data-feature={feature}
      >
        {ctaLabel ?? t('myPortal.licenseRequired.goToShop', copy.cta)}
      </Link>
    </div>
  );
}
